import { ComponentType } from "@angular/cdk/portal";
import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  InjectionToken,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  ViewChild,
  inject,
  ChangeDetectionStrategy
} from "@angular/core";
import { DashboardOverrideService } from "src/app/services/dashboard-override.service";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort, SortDirection } from "@angular/material/sort";
import { firstValueFrom, fromEvent, merge, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged, tap } from "rxjs/operators";
import { ToastService } from "src/app/services/toast.service";
import { GridDataSource } from "./grid.dataSource";
import { IGridService } from "./grid.service";
import { IFormDialogData } from "../form/form.component";
import { HeaderFilteringBaseComponent } from "../header-filtering-base/header-filtering-base.component";
import { CommonService } from "src/app/services/common.service";
import { DateFilterTypeEnum } from "src/app/enums/dateFilterType.enum";
import { ColumnDetails } from "src/app/models/columnDetails.model";
import { GridViewService } from "src/app/services/grid-view.service";
import { GridView } from "src/app/models/gridView.model";
import { GridPermissions } from "./core-grid/gridPermissions.enum";
import { ColumnFilter } from "src/app/models/columnFilter.model";
import { NestedFilterService } from "./nested-filter.service";
import { ColumnConfig } from "./core-grid/columnConfig.model";
import { WidgetConfig } from "src/app/models/dashboard";
import { TranslateService } from "@ngx-translate/core";

export const WIDGET_CONFIG = new InjectionToken<WidgetConfig>("WIDGET_CONFIG");

export const DATE_FILTER_TYPE_ENUM = "DateFilterTypeEnum";

export const ADDITIONAL_FILTERS_QUERY_PARAM = "additionalFilters";

export interface Filter {
    [key: string]: string | number | boolean;
}

export class GridLoadParams {
    filter: string = "";
    orderBy: string = "";
    sort: string = "desc";
    pageIndex: number = 0;
    pageSize: number = 10;
    coId?: string;
    filterBy: Filter = {};
}

export interface ISearchFieldData extends Record<string, unknown> {
    selected: number | string | (number | string)[];
    isMultiple: boolean;
}

@Component({
  standalone: false,
    selector: "app-base-grid",
    changeDetection: ChangeDetectionStrategy.Eager,
    template: "<div>BaseGridComponent</div>",
})
export abstract class BaseGridComponent<T>
    extends HeaderFilteringBaseComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    @Input() widgetMode = false;
    protected dashboardOverride = inject(DashboardOverrideService);
    private hasLoadedInTempView = false;

    dataSource: GridDataSource<T>;
    protected gridService: IGridService<T>;
    sortActive: string = "";
    displayedColumns: string[];
    defaultOrderBy: SortDirection = "asc";
    isLoading = false;
    coId: string;
    filterBy = {};
    subscriptions: Subscription[] = [];
    hasReadPermission: boolean = null;
    readonly GridPermissions = GridPermissions;
    protected additionalFilters: Filter = {};
    isTemporaryView = false;
    protected columnDetails: { [property: string]: ColumnDetails } = {};
    protected selectedGridView: GridView | null = null;
    currentFilters: ColumnFilter[] = [];
    activeGridFilters: { [key: string]: boolean } = {};
    columnConfigs: ColumnConfig<T>[];
    protected injectedWidgetId?: string;
    protected injectedGridViewId?: string;

    pageSize: number = 10;
    pageSizeOptions: number[] = [10, 25, 50];
    totalCount = 0;

    @ViewChild(MatPaginator, { static: false }) paginator?: MatPaginator;
    @ViewChild(MatSort, { static: false }) sort?: MatSort;
    @ViewChild("searchField", { static: false }) searchField?: ElementRef;

    isDialog: boolean;
    searchDialogRef: MatDialogRef<any>;
    searchFieldData: ISearchFieldData = null;
    selectedSearchItems: T[] = [];

    constructor(
        protected toastService: ToastService,
        protected dialog: MatDialog,
        commonService: CommonService,
        protected gridViewService: GridViewService,
        @Optional() @Inject(WIDGET_CONFIG) widgetConfig?: WidgetConfig,
    ) {
        super(commonService);
        if (widgetConfig) {
            this.widgetMode = widgetConfig.widgetMode;
            this.injectedWidgetId = widgetConfig.widgetId;
            this.injectedGridViewId = widgetConfig.gridViewId;
        }
    }

    protected get objectId(): string {
        return "";
    }

    protected get gridId(): string {
        return "";
    }

    ngOnInit(): void {
        this.isDialog = !!this.searchDialogRef;
        this.isLoading = false;
        this.dataSource = new GridDataSource<T>(
            this.gridService,
            this.toastService,
        );

        if (this.isGridViewEnabled && !this.deferGridViewInit) {
            this.initializeView();
        }

        const params: GridLoadParams = {
            filter: "",
            orderBy: this.getDefaultOrderByColumn(),
            sort: this.defaultOrderBy,
            pageIndex: 0,
            pageSize: this.getPageSize(),
            coId: this.coId,
            filterBy: this.getFilterBy(),
        };

        if (
            !this.isGridViewEnabled &&
            !this.hasDateFilter &&
            !this.hasOrgFilter &&
            !this.hasParameterFilter &&
            (this.hasReadPermission === null || this.hasReadPermission)
        ) {
            this.dataSource.load(params);
        }

        this.subscriptions.push(
            this.dataSource.isLoading$.subscribe((isLoading) => {
                this.isLoading = isLoading;
            }),
        );

        this.subscriptions.push(
            this.dataSource.count$.subscribe((count) => {
                this.totalCount = count ?? 0;
            }),
        );
    }

    protected get orgFilterKey(): string {
        return "organizationLevelId";
    }

    protected get parameterFilterKey(): string {
        return "parameterId";
    }

    protected get dateFilterColumnProperty(): string {
        return "startDate";
    }

    protected get isGridViewEnabled(): boolean {
        return false;
    }

    protected get deferGridViewInit(): boolean {
        return false;
    }

    protected getDefaultOrderByColumn(): string {
        const validColumns =
            this.displayedColumns?.filter((col) => col !== "action") || [];
        const sortableColumns =
            this.columnConfigs?.filter((cfg) => cfg.isSortable !== false) || [];
        const firstSortableColumn = validColumns.find((col) =>
            sortableColumns.some((cfg) => cfg.name === col),
        );
        return (
            firstSortableColumn ||
            (validColumns.length > 0 ? validColumns[0] : "")
        );
    }

    protected get canSaveView(): boolean {
        return !this.widgetMode;
    }

    protected getPageSize(): number {
        return this.paginator?.pageSize ?? this.pageSize;
    }

    get currentPageIndex(): number {
        return this.paginator?.pageIndex ?? 0;
    }

    get totalItems(): number {
        return this.totalCount;
    }

    get displayStart(): number {
        if (this.totalItems === 0) return 0;
        return (
            this.currentPageIndex *
                (this.paginator?.pageSize ?? this.pageSize) +
            1
        );
    }

    get displayEnd(): number {
        return Math.min(
            (this.currentPageIndex + 1) *
                (this.paginator?.pageSize ?? this.pageSize),
            this.totalItems,
        );
    }

    protected applyDrillDownOverrideFromAdditionalFilters(): void {
        const raw = this.additionalFilters[this.orgFilterKey];
        const orgId =
            typeof raw === "string"
                ? raw
                : typeof raw === "number"
                  ? String(raw)
                  : null;
        if (orgId) {
            this.dashboardOverride.setDrillDownOverride(orgId);
        }
    }

    protected initializeView(): void {
        this.isTemporaryView = false;
        if (Object.keys(this.additionalFilters).length > 0) {
            this.isTemporaryView = true;
            this.applyDrillDownOverrideFromAdditionalFilters();
            this.selectedGridView = null;
            this.onGridViewInitialized(null);
            return;
        }

        if (this.widgetMode) {
            if (this.injectedWidgetId) {
                const gridId = `WID_${this.injectedWidgetId}`;
                this.gridViewService.getDefaultView(gridId).subscribe({
                    next: (view) =>
                        view ? this.applyViewResult(view) : this.loadViewById(),
                    error: () => this.loadViewById(),
                });
            } else {
                this.loadViewById();
            }
            return;
        }

        this.gridViewService.getDefaultView(this.gridId).subscribe({
            next: (view) => this.applyViewResult(view),
            error: () => this.applyViewResult(null),
        });
    }

    private applyViewResult(view: GridView | null): void {
        this.selectedGridView = view;
        this.onGridViewInitialized(view);
    }

    private loadViewById(): void {
        if (this.injectedGridViewId) {
            this.gridViewService.getById(this.injectedGridViewId).subscribe({
                next: (view) => this.applyViewResult(view),
                error: () => this.applyViewResult(null),
            });
        } else {
            this.applyViewResult(null);
        }
    }

    protected startGridViewInitialization(): void {
        if (!this.isGridViewEnabled || this.selectedGridView !== null) return;
        this.initializeView();
    }

    async getColumnDetailsAsync(): Promise<{
        [property: string]: ColumnDetails;
    }> {
        if (Object.keys(this.columnDetails).length > 0) {
            return this.columnDetails;
        }
        const details = await firstValueFrom(
            this.gridService.getFieldConfiguration(),
        );
        this.columnDetails = details || {};
        return this.columnDetails;
    }

    protected setupNestedFilterRules(): void {
        if (
            this.columnConfigs &&
            this.columnConfigs.length > 0 &&
            this.columnDetails
        ) {
            const rules =
                NestedFilterService.getInstance().createNestedFilterRules(
                    this.columnDetails,
                    this.columnConfigs,
                );
            this.dataSource.setNestedFilterRules(rules);
        }
    }

    getFilterBy(): Filter {
        let filterBy: Filter = {};

        if (this.inheritsHeaderDateFilter && this.selectedDateRange && !this.isTemporaryView) {
            filterBy[DATE_FILTER_TYPE_ENUM] = JSON.stringify({
                dateFilterType:
                    this.selectedDateRange?.id ?? DateFilterTypeEnum.AllDates,
                endDate: this.selectedDateRange?.end ?? 0,
                previousEndDate: this.selectedDateRange?.prevEnd ?? 0,
                previousStartDate: this.selectedDateRange?.prevStart ?? 0,
                startDate: this.selectedDateRange?.start ?? 0,
            });
        }
        if (this.hasOrgFilter && this.selectedOrg && !this.isTemporaryView) {
            filterBy[this.orgFilterKey] = this.selectedOrg.id;
        }
        if (this.hasParameterFilter && this.selectedParameter) {
            filterBy[this.parameterFilterKey] = this.selectedParameter.id;
        }

        for (const [key, value] of Object.entries(this.additionalFilters)) {
            if (key === this.orgFilterKey && this.activeGridFilters[key] === false) continue;
            if (key === DATE_FILTER_TYPE_ENUM && this.activeGridFilters[this.dateFilterColumnProperty] === false) continue;
            filterBy[key] = value;
        }

        const viewFilters = this.selectedGridView?.settingsJson?.filters ?? {};

        const selectedFilters = Object.entries(viewFilters)
            .filter(([key, _]) => {
                if (Object.keys(this.activeGridFilters).length === 0) {
                    return true;
                }

                if (this.isActiveGridFilter(key)) {
                    return true;
                }

                // Handle OperatorTypeEnum keys like "OperatorTypeEnum.4_ihLabResults.twa"
                const underscoreIndex = key.indexOf("_");
                if (
                    key.toLowerCase().startsWith("operatortypeenum.") &&
                    underscoreIndex !== -1
                ) {
                    if (
                        this.isActiveGridFilter(
                            key.substring(underscoreIndex + 1),
                        )
                    ) {
                        return true;
                    }
                }

                // Fallback: extract field after last underscore
                const fieldMatch = key.match(/_(\w+)$/);
                return fieldMatch
                    ? this.activeGridFilters[fieldMatch[1]] === true
                    : false;
            })
            .reduce(
                (acc, [key, value]) => {
                    // Fix the key transformation - preserve the correct format
                    let finalKey = key;
                    if (key.toLowerCase().startsWith("operatortypeenum.")) {
                        // Convert "operatorTypeEnum.4_field" back to "OperatorTypeEnum.4_field"
                        finalKey = `OperatorTypeEnum${key.slice("operatorTypeEnum".length)}`;
                    }
                    acc[finalKey] = value;
                    return acc;
                },
                {} as Record<string, string>,
            );

        Object.assign(filterBy, selectedFilters);

        return filterBy;
    }

    private isActiveGridFilter(key: string): boolean {
        if (this.activeGridFilters[key] === true) {
            return true;
        }
        const lowerKey = key.toLowerCase();
        return Object.keys(this.activeGridFilters).some(
            (activeKey) =>
                activeKey.toLowerCase() === lowerKey &&
                this.activeGridFilters[activeKey] === true,
        );
    }

    ngAfterViewInit(): void {
        if (this.sort && this.paginator) {
            this.subscriptions.push(
                this.sort.sortChange.subscribe(
                    () => (this.paginator.pageIndex = 0),
                ),
                merge(this.sort.sortChange, this.paginator.page)
                    .pipe(tap(() => this.loadPage()))
                    .subscribe(),
            );
        }

        if (this.searchField) {
            this.subscriptions.push(
                fromEvent(this.searchField.nativeElement, "keyup")
                    .pipe(
                        debounceTime(200),
                        distinctUntilChanged(),
                        tap(() => {
                            if (this.paginator) this.paginator.pageIndex = 0;
                            this.loadPage();
                        }),
                    )
                    .subscribe(),
            );
        }
    }

    loadPage(): void {
        const gridSort = this.selectedGridView?.settingsJson?.sort;
        if (gridSort && !this.sortActive) {
            this.sortActive = gridSort.sortBy || this.getDefaultOrderByColumn();
            this.defaultOrderBy = gridSort.sortOrder || "asc";
            if (this.sort) {
                this.sort.active = this.sortActive;
                this.sort.direction = this.defaultOrderBy;
            }
        }

        const params: GridLoadParams = {
            filter: this.searchField?.nativeElement?.value ?? "",
            orderBy: this.sortActive || this.getDefaultOrderByColumn(),
            sort: this.defaultOrderBy || "asc",
            pageIndex: this.paginator?.pageIndex ?? 0,
            pageSize: this.paginator?.pageSize ?? this.getPageSize(),
            coId: this.coId,
            filterBy: this.getFilterBy(),
        };

        if (this.hasReadPermission === null || this.hasReadPermission) {
            this.dataSource.load(params);
        } else {
            this.isLoading = false;
        }
    }

    clearMatSort(): void {
        if (this.sort) {
            this.sort.active = "";
            this.sort.direction = "";
        }
        this.sortActive = "";
        this.defaultOrderBy = "asc";
    }

    loadComponentData(): void {
        if (this.isTemporaryView && this.hasLoadedInTempView) return;
        if (!this.isGridViewEnabled || this.selectedGridView) {
            if (this.isTemporaryView) this.hasLoadedInTempView = true;
            this.loadPage();
        }
    }

    ngOnDestroy(): void {
        this.dashboardOverride.clearDrillDownOverride();
        super.ngOnDestroy();
    }

    openDialog<T1, T2 extends IFormDialogData>(
        dialogComponent: ComponentType<T1>,
        data: T2,
        isReadonly: boolean = false,
        width: string = "400px",
    ) {
        const dialogRef = this.dialog.open(dialogComponent, {
            data: {
                ...data,
                isReadonly: isReadonly,
                columnDetails: this.columnDetails,
            },
            width: width,
            disableClose: true,
        });

        this.handleDialogClose(dialogRef);
    }

    openDeleteDialog<T1, T2 extends IFormDialogData>(
        dialogComponent: ComponentType<T1>,
        data: T2,
        service: any,
        title: string,
        width: string = "440px",
        panelClass = "delete-confirmation-dialog",
        reloadAction?: () => void,
    ) {
        const dialogRef = this.dialog.open(dialogComponent, {
            data: {
                id: data.id,
                formDeletes: true,
                service: service,
                title: title,
            },
            width: width,
            disableClose: true,
            ariaLabel: title,
            panelClass: panelClass,
        });
        this.handleDialogClose(dialogRef, reloadAction);
    }

    protected isSavedDialogResult(result: unknown): boolean {
        return result !== false && result !== "";
    }

    handleDialogClose(
        dialogRef: any,
        reloadAction?: (result?: unknown) => void,
        successAction?: () => any,
        cancelAction?: () => any,
    ) {
        dialogRef.afterClosed().subscribe((result) => {
            if (this.isSavedDialogResult(result)) {
                reloadAction ? reloadAction(result) : this.loadPage();
                if (successAction) {
                    successAction();
                }
            } else {
                if (cancelAction) {
                    cancelAction();
                }
            }
        });
    }

    protected onGridViewInitialized(view: GridView | null): void {}
}
